import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import PropTypes from 'prop-types';

import YearSelector from '../../../Common/Selectors/YearSelectorSingle';
import { creators as FinancialYTDActions } from '../../../Reducers/Reporting/FinancialYTD';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import { financialMonth, printFormat } from '../../../Utils/Functions';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import BottomChart from './BottomChart';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const FinancialPerformanceYTD = ({  
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,  
  selectedCompany,
  tableStructure,
  financialPerformanceYTDSetting,
  profitSetting,
  tabIndex,
  bubbleFlag
}) => {
  const dispatch = useDispatch();

  // ---- Safe Redux selectors (with defaults) ----
  const fpySummary = useSelector(
    (s) => s?.Reporting?.Summary?.reportingSummaries?.financialYTD ?? ''
  );
  const summaryData = useSelector(
    (s) => s?.Reporting?.financialYTD?.summaryData ?? EMPTY_OBJECT
  );
  const detailData = useSelector(
    (s) => (Array.isArray(s?.Reporting?.financialYTD?.detailData) ? s.Reporting.financialYTD.detailData : EMPTY_ARRAY)
  );
  const lastFetchedCompany = useSelector(
    (s) => s?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (s) => s?.Reporting?.Default?.defaultDimDate ?? null,
  );

  // ---- Local state ----
  const [label, setLabel] = useState(String(defaultYear));
  const [selectedYears, setSelectedYears] = useState([defaultYear]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedOption, setSelectedOption] = useState('Forecast');
  const [showPercent, setShowPercent] = useState(false);
  const [profitName, setProfitName] = useState('Profit / Loss');
  const [pprofitName, setPProfitName] = useState('% Profit');
  const isMounted = useRef(false);

  const buildRollingMonths = useCallback(
    (year) => {
      const months = [];
      for (let i = defaultStartMonth; i <= 12; i++) {
        months.push((year - 1) * 10000 + i * 100 + 1);
      }
      for (let i = 1; i < defaultStartMonth; i++) {
        months.push(year * 10000 + i * 100 + 1);
      }
      return months;
    },
    [defaultStartMonth],
  );

  const dispatchFinancialYtdRequests = useCallback(() => {
    const months = buildRollingMonths(defaultYear);
    const selectedYearMonth = appdata.getLocalItem('fpySelectedYearMonth') || defaultDimDate;
    dispatch(FinancialYTDActions.fytdSummaryRequest(months));
    dispatch(FinancialYTDActions.fytdDetailRequest(selectedYearMonth));
  }, [buildRollingMonths, defaultYear, defaultDimDate, dispatch]);

  // ---- Resize handler (just forces a re-render) ----
  const onResize = useCallback(() => {
    if (isMounted.current) {
      setLabel((l) => l + '');
    }
  }, []);

  // ---- Initial mount ----
  useEffect(() => {
    isMounted.current = true;
    window.addEventListener('resize', onResize);

    setLabel(String(defaultYear));
    setSelectedYears([defaultYear]);
    setSelectedMonths([financialMonth(defaultMonth, defaultStartMonth) - 1]);

    const topItems =
      appdata.getLocalItem('fpyTopItems') ||
      [
        {
          year: defaultYear,
          month: financialMonth(defaultMonth, defaultStartMonth) - 1,
        },
      ];
    setSelectedTopItems(topItems);

    // financialPerformanceYTDSetting can be string/object/undefined
    try {
      let setting = null;
      if (typeof financialPerformanceYTDSetting === 'string' && financialPerformanceYTDSetting.trim()) {
        setting = JSON.parse(financialPerformanceYTDSetting);
      } else if (financialPerformanceYTDSetting && typeof financialPerformanceYTDSetting === 'object') {
        setting = financialPerformanceYTDSetting;
      }
      if (setting) {
        if (setting.selectedOption !== undefined) setSelectedOption(setting.selectedOption);
        if (setting.showPercent !== undefined) setShowPercent(!!setting.showPercent);
      } else {
        dispatch(
          ReportSettingActions.reportSettingUpdateRequest({
            FinancialPerformanceYTD: JSON.stringify({
              selectedOption: 'Forecast',
              showPercent: false,
            }),
          })
        );
      }
    } catch {
      // ignore bad JSON; keep defaults
    }

    // profitSetting can be string/object/undefined
    try {
      let p = null;
      if (typeof profitSetting === 'string' && profitSetting.trim()) {
        p = JSON.parse(profitSetting);
      } else if (profitSetting && typeof profitSetting === 'object') {
        p = profitSetting;
      }
      if (p) {
        if (p.profitName) setProfitName(p.profitName);
        if (p.pprofitName) setPProfitName(p.pprofitName);
      } else {
        dispatch(
          ReportSettingActions.reportSettingUpdateRequest({
            ProfitTitle: JSON.stringify({
              profitName: 'Profit / Loss',
              pprofitName: '% Profit',
            }),
          })
        );
      }
    } catch {
      // ignore bad JSON; keep defaults
    }

    return () => {
      isMounted.current = false;
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Watch for prop changes (FY/month) ----
  useEffect(() => {
    if (!isMounted.current) return;

    setSelectedYears([defaultYear]);
    setLabel(String(defaultYear));
    setSelectedMonths([financialMonth(defaultMonth, defaultStartMonth) - 1]);

    const topItems = [
      {
        year: defaultYear,
        month: financialMonth(defaultMonth, defaultStartMonth) - 1,
      },
    ];
    setSelectedTopItems(topItems);
    appdata.setLocalItem('fpyTopItems', topItems);

    if (!financialPerformanceYTDSetting) {
      dispatch(
        ReportSettingActions.reportSettingUpdateRequest({
          FinancialPerformanceYTD: JSON.stringify({
            selectedOption: 'Forecast',
            showPercent: false,
          }),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultYear, defaultMonth, defaultDimDate, defaultStartMonth, financialPerformanceYTDSetting, dispatch]);

  useEffect(() => {
    if (!isMounted.current) return;

    if (!lastFetchedCompany || !lastFetchedDimDate) {
      dispatchFinancialYtdRequests();
    }
  }, [dispatchFinancialYtdRequests, lastFetchedCompany, lastFetchedDimDate]);

  useEffect(() => {
    if (!isMounted.current) return;
    if (!lastFetchedCompany || !lastFetchedDimDate) return;

    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (valuesChanged) {
      dispatchFinancialYtdRequests();
    }
  }, [dispatchFinancialYtdRequests, selectedCompany, defaultDimDate, lastFetchedCompany, lastFetchedDimDate]);

  // ---- Re-read profit titles when tab changes (if provided) ----
  useEffect(() => {
    try {
      let p = null;
      if (typeof profitSetting === 'string' && profitSetting.trim()) {
        p = JSON.parse(profitSetting);
      } else if (profitSetting && typeof profitSetting === 'object') {
        p = profitSetting;
      }
      if (p) {
        if (p.profitName) setProfitName(p.profitName);
        if (p.pprofitName) setPProfitName(p.pprofitName);
      }
    } catch {
      // ignore bad JSON
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex]);

  // ---- Handlers ----
  const handleYear = async (event) => {
    const nextYear = event?.selectedYears?.[0];
    if (!nextYear) return;

    let _defaultdimdate;
    const months = [];
    if (defaultMonth <= defaultStartMonth) {
      _defaultdimdate = printFormat.dateYYYYMMDD(
        nextYear - 1,
        financialMonth(defaultMonth, defaultStartMonth)
      );
    } else {
      _defaultdimdate = printFormat.dateYYYYMMDD(
        nextYear,
        financialMonth(defaultMonth, defaultStartMonth)
      );
    }

    for (let i = defaultStartMonth; i <= 12; i++) {
      months.push((nextYear - 1) * 10000 + i * 100 + 1);
    }
    for (let i = 1; i < defaultStartMonth; i++) {
      months.push(nextYear * 10000 + i * 100 + 1);
    }

    dispatch(FinancialYTDActions.fytdSummaryRequest(months));
    dispatch(FinancialYTDActions.fytdDetailRequest(_defaultdimdate));

    setSelectedYears(event.selectedYears);
    setLabel(String(nextYear));
    if (event.selectedMonths) setSelectedMonths(event.selectedMonths);
    if (event.selectedTopItems) setSelectedTopItems(event.selectedTopItems);
  };

  const handleFilter = async (event) => {
    if (event.selectedYears) setSelectedYears(event.selectedYears);
    if (event.label) setLabel(event.label);
    if (event.selectedMonths) setSelectedMonths(event.selectedMonths);

    if (event.selectedTopItems) {
      setSelectedTopItems(event.selectedTopItems);
      appdata.setLocalItem('fpyTopItems', event.selectedTopItems);
    }

    if (event.selectedOption !== undefined) setSelectedOption(event.selectedOption);
    if (event.showPercent !== undefined) setShowPercent(!!event.showPercent);
    if (event.profitName) setProfitName(event.profitName);
    if (event.pprofitName) setPProfitName(event.pprofitName);

    if (event.selectedYearMonth) {
      dispatch(FinancialYTDActions.fytdDetailRequest(event.selectedYearMonth));
      appdata.setLocalItem('fpySelectedYearMonth', event.selectedYearMonth);
    }

    if (event.selectedOption !== undefined || event.showPercent !== undefined) {
      dispatch(
        ReportSettingActions.reportSettingUpdateRequest({
          FinancialPerformanceYTD: JSON.stringify({
            selectedOption:
              event.selectedOption !== undefined ? event.selectedOption : selectedOption,
            showPercent:
              event.showPercent !== undefined ? !!event.showPercent : !!showPercent,
          }),
        })
      );
    }

    if (event.profitName || event.pprofitName) {
      dispatch(
        ReportSettingActions.reportSettingUpdateRequest({
          ProfitTitle: JSON.stringify({
            profitName: event.profitName ? event.profitName : profitName,
            pprofitName: event.pprofitName ? event.pprofitName : pprofitName,
          }),
        })
      );
    }
  };

  // ---- Safe data for charts ----
  const safeSummary = summaryData && typeof summaryData === 'object' ? summaryData : {};
  const safeDetail = Array.isArray(detailData) ? detailData : [];

  return (
    <div style={{ height: '89vh' }}>
      <div className="wrapper" id="fpy_year_selector">
        {label ? (
          <YearSelector
            selectedYears={selectedYears}
            label={label}
            onChange={handleYear}
          />
        ) : null}
      </div>

      <div>
        <TopChart
          summaryData={safeSummary}
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          selectedTopItems={selectedTopItems}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
          handleFilter={handleFilter}
        />
        <BottomChart
          detailData={safeDetail}
          selectedMonths={selectedMonths}
          tableStructure={tableStructure}
          selectedOption={selectedOption}
          showPercent={showPercent}
          profitName={profitName}
          pprofitName={pprofitName}
          handleFilter={handleFilter}
        />
      </div>

      {bubbleFlag && !!fpySummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
        >
          <Tooltip
            title={fpySummary}
            placement="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                },
              },
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

FinancialPerformanceYTD.propTypes = {  
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  selectedCompany: PropTypes.any,
  tableStructure: PropTypes.any,
  financialPerformanceYTDSetting: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  profitSetting: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  tabIndex: PropTypes.number,
  bubbleFlag: PropTypes.bool,
};

FinancialPerformanceYTD.defaultProps = {
  tableStructure: null,
  financialPerformanceYTDSetting: null,
  profitSetting: null,
  tabIndex: 0,
  bubbleFlag: false,
};

export default FinancialPerformanceYTD;
